/**
 * CouponsListPage - Admin coupon/discount code management
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getCoupons,
  toggleCoupon,
} from '../../../api/admin';
import type { Coupon } from '../../../api/admin';
import { formatPrice } from '../../../api/events';
import { parseLocalDate } from '../../../utils/date';

type CouponFilter = 'all' | 'active' | 'inactive';

function formatDiscount(type: string, value: number): string {
  if (type === 'fixed') return formatPrice(value);
  return `${value / 100}%`;
}

function formatAppliesTo(val: string): string {
  const labels: Record<string, string> = {
    total: 'Entire Order',
    sessions_only: 'Sessions Only',
    addons_only: 'Add-ons Only',
  };
  return labels[val] || val;
}

export default function CouponsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<CouponFilter>(
    (searchParams.get('filter') as CouponFilter) || 'all'
  );

  useEffect(() => {
    loadCoupons();
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('filter', filter);
    setSearchParams(params);
  }, [filter]);

  const loadCoupons = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: { is_active?: boolean } = {};
      if (filter === 'active') params.is_active = true;
      if (filter === 'inactive') params.is_active = false;

      const data = await getCoupons(Object.keys(params).length > 0 ? params : undefined);
      setCoupons(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (coupon: Coupon, e: React.MouseEvent) => {
    e.stopPropagation();
    // Optimistic update
    setCoupons((prev) =>
      prev.map((c) =>
        c.id === coupon.id ? { ...c, is_active: !c.is_active } : c
      )
    );
    try {
      await toggleCoupon(coupon.id);
    } catch {
      // Revert on error
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === coupon.id ? { ...c, is_active: coupon.is_active } : c
        )
      );
    }
  };

  const formatDate = (date: string) => {
    // expiry_date is a date-only string (YYYY-MM-DD) — use parseLocalDate to avoid
    // UTC midnight timezone shift (bare new Date(date) would show the wrong day in
    // any UTC-offset timezone).
    return parseLocalDate(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading && coupons.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Coupons</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Create and manage discount codes for bookings.
          </p>
        </div>
        <Link to="/admin/coupons/new" className="btn-primary min-h-[44px] flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">New </span>Coupon
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex gap-1">
          {(['all', 'active', 'inactive'] as CouponFilter[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                filter === tab
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-warm-600 hover:text-warm-900 hover:bg-warm-100'
              }`}
            >
              {tab === 'all' ? 'All' : tab === 'active' ? 'Active' : 'Inactive'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Coupons List */}
      {coupons.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No coupons found</h3>
          <p className="text-warm-600 mb-6">
            {filter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Create your first coupon to offer discounts on bookings.'}
          </p>
          {filter === 'all' && (
            <Link to="/admin/coupons/new" className="btn-primary">
              Create Coupon
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Code</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Discount</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Applies To</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Uses</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Expiry</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-warm-500 uppercase tracking-wider">Toggle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-200">
                {coupons.map((coupon) => (
                  <tr
                    key={coupon.id}
                    onClick={() => navigate(`/admin/coupons/${coupon.id}/edit`)}
                    className="hover:bg-warm-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono font-medium text-warm-900">{coupon.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-900 font-medium">
                        {formatDiscount(coupon.discount_type, coupon.discount_value)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-600">{formatAppliesTo(coupon.applies_to)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-900">
                        {coupon.usage_count ?? 0}/{coupon.max_uses != null ? coupon.max_uses : '\u221E'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-600 text-sm">
                        {coupon.expiry_date ? formatDate(coupon.expiry_date) : 'No expiry'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          coupon.is_active
                            ? 'bg-success-50 text-success-700'
                            : 'bg-warm-100 text-warm-600'
                        }`}
                      >
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => handleToggle(coupon, e)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          coupon.is_active ? 'bg-primary-600' : 'bg-warm-200'
                        }`}
                        role="switch"
                        aria-checked={coupon.is_active}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            coupon.is_active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {coupons.map((coupon) => (
              <div
                key={coupon.id}
                onClick={() => navigate(`/admin/coupons/${coupon.id}/edit`)}
                className="bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-medium text-warm-900">{coupon.code}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      coupon.is_active
                        ? 'bg-success-50 text-success-700'
                        : 'bg-warm-100 text-warm-600'
                    }`}
                  >
                    {coupon.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-warm-900 font-medium">
                    {formatDiscount(coupon.discount_type, coupon.discount_value)}
                  </span>
                  <span className="text-warm-400">|</span>
                  <span className="text-warm-600 text-sm">{formatAppliesTo(coupon.applies_to)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-warm-600">
                    Uses: {coupon.usage_count ?? 0}/{coupon.max_uses != null ? coupon.max_uses : '\u221E'}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-warm-500">
                      {coupon.expiry_date ? formatDate(coupon.expiry_date) : 'No expiry'}
                    </span>
                    <button
                      onClick={(e) => handleToggle(coupon, e)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        coupon.is_active ? 'bg-primary-600' : 'bg-warm-200'
                      }`}
                      role="switch"
                      aria-checked={coupon.is_active}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          coupon.is_active ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
